import { apiClient } from "~/lib/client";
import { useMutation } from "@tanstack/react-query";
import { handleError, handleResponse } from "~/utils/response";
import { ErrorResponse, SuccessResponse } from "~/types";
import { useEncounterIdStore } from "../../pages/welcome/store/use-encounter-id-store";

// Define a type for the raw response data
export type PatientOnboardingData = {
  message: string;
  success: string;
  consultation_id: string;
}

// Define the combined response type (including success/error structure)
export type PatientOnboardingResponse = SuccessResponse<PatientOnboardingData> | ErrorResponse;

type PatientOnboardingVariables = {
  userId: string;
  gender: string;
  weight: string;
  height: string;
  bmi: string;
  glpIntake: string;
  healthCondition: string[];
  events: string[];
  history: string;
  lastintake: string;
  onMedication: string;
  medications: string[];
  recentdose: string;
  targetWeight: string;
  timeframe: string;
};

export const useOnboardingMutation = () => {
  const {setEncounterId} = useEncounterIdStore()
  
  return useMutation<PatientOnboardingData, unknown, PatientOnboardingVariables>({
    mutationFn: async (variables: PatientOnboardingVariables) => {
      try {
        const response = await apiClient("/onboarding", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<PatientOnboardingData>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        
        console.log("OnboardedData", result);
        setEncounterId(result.data.consultation_id);
        return result.data; // Return just the data part
      } catch (error) {
        throw error; // Rethrow the error instead of returning it
      }
    },
  });
};

type BookingConfirmationVariables = {
  email: string;
  booking_details: {
    patient_name: string;
    appointment_date: string;
    appointment_time: string;
    appointment_type: string;
  };
};

type BookingConfirmationResponse = SuccessResponse<{
  message: string;
  success: string;
}> | ErrorResponse;

export const useBookingConfirmationMutation = () => {
  return useMutation<BookingConfirmationResponse, unknown, BookingConfirmationVariables>({
    mutationFn: async (variables: BookingConfirmationVariables) => {
      try {
        const response = await apiClient("/send-booking-confirmation", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<BookingConfirmationResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
  });
};

type ContactUsVariables = {
  full_name: string;
  email: string;
  subject: string;
  message: string;
};

type ContactUsResponse = SuccessResponse<{
  message: string;
  status: string;
}> | ErrorResponse;


export const useContactUsMutation = () => {
  return useMutation<ContactUsResponse, unknown, ContactUsVariables>({
    mutationFn: async (variables: ContactUsVariables) => {
      try {
        const response = await apiClient("/contact-us", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<ContactUsResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
  });
};

type MedicalPlanVariables = {
  program: string;
  session_id: string | null;
  medication: {
    name: string;
    cost: number;
  };
  consultation: {
    name: string;
    cost: number;
  };
  appointment: {
    title: string;
    description: string;
    location: string;
    date: string;
    time: string;
    timezone: string;
    program: string;
    session_id: string | null;
    additionalProp1: Record<string, unknown>;
  };
};

type MedicalPlanSuccessData = {
  message: string;
  status: string;
  payment?: {
    message: string;
    payment_type: string;
    status: string;
    type_id: string;
    url: string;
  };
  plan_id: string;
};

// type MedicalPlanResponse = SuccessResponse<MedicalPlanSuccessData> | ErrorResponse;

export const useMedicalPlanMutation = () => {
  const {setEncounterId} = useEncounterIdStore()
  return useMutation<MedicalPlanSuccessData, unknown, MedicalPlanVariables>({
    mutationFn: async (variables: MedicalPlanVariables) => {
      try {
        const response = await apiClient("/users/medical-plan", {
          method: "POST",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<MedicalPlanSuccessData>(response);
        console.log("MedicalPlanSuccessData", result);


        if (!result.success) {
          throw new Error(result.error);
        }
        setEncounterId(result.data.plan_id);
        console.log("planid", result.data.plan_id)
        return result.data;
      } catch (error) {
        const handledError = handleError(error);
        throw new Error(handledError.error);
      }
    },
  });
};

type SupplementaryQuestionnaireVariables = {
  question_answer: {
    additionalProp1?: Record<string, unknown>;
  };
};

type SupplementaryQuestionnaireResponse = SuccessResponse<{
  message: string;
  status: string;
}> | ErrorResponse;

export const useSupplementaryQuestionnaireMutation = () => {
  return useMutation<SupplementaryQuestionnaireResponse, unknown, SupplementaryQuestionnaireVariables>({
    mutationFn: async (variables: SupplementaryQuestionnaireVariables) => {
      try {
        const response = await apiClient("/users/medical-plan/supplementary-questionnaire", {
          method: "PUT",
          body: JSON.stringify(variables),
        });

        const result = await handleResponse<SupplementaryQuestionnaireResponse>(response);

        if (!result.success) {
          throw new Error(result.error);
        }
        return result.data;
      } catch (error) {
        return handleError(error);
      }
    },
  });
};
